import React, { useEffect, useMemo, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    LabelList,
    Customized,
} from "recharts";


const GraficoDashboard = ({ titulo, data, openSubGraficos, handleLegendClick, hiddenKeys }) => {
    const formatNumber = (num) => {
        if (num >= 1e9) return Math.floor(num / 1e8) / 10 + 'B';
        if (num >= 1e6) return Math.floor(num / 1e5) / 10 + 'M';
        if (num >= 1e3) return Math.floor(num / 1e2) / 10 + 'K';
        return num.toFixed(0);
    };

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const updateSize = () => setIsMobile(window.innerWidth < 768);
        updateSize();
        window.addEventListener("resize", updateSize);
        return () => window.removeEventListener("resize", updateSize);
    }, []);

    const maxTotal = useMemo(() => {
        const max = Math.max(...data.map(d =>
            (d.aprovado || 0) + (d.pendente || 0) + (d.cancelado || 0)
        ));
        return Math.floor(max);
    }, [data]);

    const xDomain = [0, maxTotal * (isMobile ? 1.2 : 1.1)];

    return (
        <div id={titulo} style={{ maxHeight: 300, overflowY: 'auto' }}>
            <ResponsiveContainer width="100%" height={Math.max(250, data.length * 40)}>
                <BarChart data={data} layout="vertical" barSize={30}>
                    <CartesianGrid strokeDasharray="3 3" />

                    <XAxis
                        type="number"
                        domain={xDomain}
                        tickFormatter={formatNumber}
                    />

                    <YAxis
                        type="category"
                        dataKey="nome"
                        interval={0}
                        tick={{ fontSize: 10 }}
                        tickFormatter={(value) =>
                            value.length > 12 ? value.substring(0, 10) + "..." : value
                        }
                        minWidth={100}
                    />

                    <Tooltip position={{ x: 150, y: 0 }} />
                    <Legend onClick={handleLegendClick} />

                    {["aprovado", "pendente", "cancelado"].map((key, index) => (
                        <Bar
                            key={key}
                            dataKey={key}
                            stackId="a"
                            fill={["#1f77b4", "#ff7f0e", "#FF6666"][index]}
                            hide={hiddenKeys[key]}
                        >
                            <LabelList
                                dataKey={key}
                                position="inside"
                                fill="#fff"
                                style={{ fontWeight: "bold" }}
                                formatter={(value) => (value === 0 ? "" : formatNumber(value))}
                            />
                        </Bar>
                    ))}

                    <Customized
                        component={({ xAxisMap, yAxisMap, data: chartData }) => {
                            const xScale = xAxisMap[0].scale;
                            const yScale = yAxisMap[0].scale;
                            const maxX = xScale.range()[1];

                            return chartData.map((entry, index) => {
                                const total = (entry.aprovado || 0) + (entry.pendente || 0) + (entry.cancelado || 0);
                                let xPos = xScale(total) + 5;
                                const barHeight = yScale.bandwidth();
                                const yPos = yScale(entry.nome) + barHeight / 2;

                                if (xPos + 60 > maxX) xPos = maxX - 60;

                                return (
                                    <g key={`total-${index}`}>
                                        <text
                                            x={xPos}
                                            y={yPos}
                                            fill="black"
                                            fontSize={12}
                                            fontWeight="bold"
                                            textAnchor="start"
                                            alignmentBaseline="middle"
                                            stroke="black"
                                            letterSpacing={1}
                                            strokeWidth={0.5}
                                        >
                                            {formatNumber(total)}
                                        </text>
                                        {entry.subGraficos?.length > 0 && (
                                            <foreignObject
                                                x={xPos + 40}
                                                y={yPos - 12}
                                                width={30}
                                                height={24}
                                            >
                                                <button
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        backgroundColor: '#4CAF50',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        fontSize: '10px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        padding: 0
                                                    }}
                                                    onClick={() => openSubGraficos(entry.subGraficos, entry.nome)}
                                                >
                                                    ▶
                                                </button>
                                            </foreignObject>
                                        )}
                                    </g>
                                );
                            });
                        }}
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default function ChartDashBoard({ data, titulo, subGraficos, setSubGraficoData, setSubGraficoTitle }) {
    const [top5, setTop5] = useState(true);
    const [hiddenKeys, setHiddenKeys] = useState({
        aprovado: false,
        pendente: false,
        cancelado: false,
    });

    const openSubGraficos = (data, title) => {
        setSubGraficoData(data);
        setSubGraficoTitle("Revenda - " + title);
        subGraficos.current.Show(title);
    }

    const handleLegendClick = (e) => {
        const { dataKey } = e;
        setHiddenKeys((prev) => ({
            ...prev,
            [dataKey]: !prev[dataKey],
        }));
    };

    let totais = data.map(obj => obj.aprovado + obj.pendente + obj.cancelado);
    totais.sort((a, b) => b - a);
    let filteredData = data.sort((a, b) => (b.aprovado + b.pendente + b.cancelado) - (a.aprovado + a.pendente + a.cancelado)).slice(0, 5);

    const scrollToTop = () => {
        const element = document.getElementById(titulo);
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <div style={{ position: "relative", maxHeight: Math.max(350, top5 ? filteredData.length : data.length * 40) + "px", height: 350 }}>
            <h2 className="text-start md-text-center" style={{ textAlign: "center", marginBottom: "20px" }}>{titulo}</h2>
            {totais.length > 5
                && <div style={{ position: "absolute", top: 0, right: 0, display: "flex", gap: "10px" }}>
                    <button
                        onClick={() => { setTop5(true); scrollToTop() }}
                        style={{
                            backgroundColor: top5 ? "#1f77b4" : "#ccc",
                            color: "white",
                            padding: "5px 10px",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                    >
                        Top 5
                    </button>
                    <button
                        onClick={() => setTop5(false)}
                        style={{
                            backgroundColor: !top5 ? "#1f77b4" : "#ccc",
                            color: "white",
                            padding: "5px 10px",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                    >
                        Todos
                    </button>
                </div>}
            <GraficoDashboard titulo openSubGraficos={openSubGraficos} data={top5 ? filteredData : data} handleLegendClick={handleLegendClick} hiddenKeys={hiddenKeys} />
        </div >
    );
}